import React from "react";
import { Tab } from "@mui/material";
import { useNavigate } from "react-router-dom";

interface LinkTabProps {
  label?: string;
  icon?: React.ReactElement;
  href: string;
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number | null>>;
  isSelected: boolean;
  onClick?: () => void;
  component?: string;
}

const LinkTab: React.FC<LinkTabProps> = ({
  label,
  icon,
  href,
  value,
  setValue,
  isSelected,
  onClick,
}) => {
  const navigate = useNavigate();

  const handleMouseDown = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // ✅ Middle mouse button → open in new tab immediately on mousedown
    if (event.button === 1) {
      event.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    // ✅ Ctrl+Click or Cmd+Click → open in new tab
    if (event.button === 0 && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // ✅ Block click from firing if Ctrl/Cmd/middle was held
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // ✅ Normal left click → prevent full page reload, use SPA navigation
    event.preventDefault();
    navigate(href);
    setValue(value);
    localStorage.setItem("activeTab", value.toString());
    if (onClick) onClick();
  };

  return (
    // ✅ <a> tag gives browser the href context for right-click "Open in new tab"
    // ✅ onMouseDown intercepts Ctrl+click / middle-click BEFORE MUI swallows it
    // ✅ onClick prevents full page reload on normal left clicks
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{
        display: "contents", // invisible wrapper — no effect on layout
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Tab
        icon={icon}
        label={label}
        value={value}
        sx={{
          fontSize: "12px",
          paddingLeft: "16px",
          textAlign: "center",
          justifyContent: "flex-start",
          textDecoration: "none",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "none",
          },
        }}
      />
    </a>
  );
};

export default LinkTab;